/**
 * The one way the app talks to the server.
 *
 * Everything lives behind `/api` on this same origin, so there is no base URL
 * to configure and no token to keep: the session is an HttpOnly cookie the
 * browser attaches itself. Every request therefore only needs
 * `credentials: 'same-origin'`.
 *
 * Errors always arrive as `{ error: "sentence for the resident" }` with a 4xx
 * or 5xx status; they become an `ApiError` carrying both, and `errorMessage()`
 * turns any thrown thing into something safe to put in a toast.
 */

export class ApiError extends Error {
    readonly status: number;

    constructor(status: number, message: string) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}

export const isApiError = (err: unknown): err is ApiError => err instanceof ApiError;

// ---------------------------------------------------------------------------
// Signed-out handling
//
// A 401 from any endpoint means the cookie is gone or expired. We clear the
// cached user and tell AuthContext, which falls back to its signed-out state
// and lets the router show the sign-in page. We deliberately do NOT navigate
// from here: a redirect issued by the HTTP layer races the router and can
// bounce a resident between /login and the page they wanted.
// ---------------------------------------------------------------------------

export interface SessionUser {
    id: string;
    email: string;
    full_name: string;
    role: 'admin' | 'resident';
    account_status: 'pending' | 'approved' | 'rejected' | 'moved_out';
    email_verified: boolean;
}

let cachedUser: SessionUser | null = null;
const signedOutListeners = new Set<() => void>();

/** Latest user seen from `GET /api/auth/me`, or null when signed out. */
export const getCachedUser = (): SessionUser | null => cachedUser;
export const setCachedUser = (user: SessionUser | null): void => { cachedUser = user; };

/** Subscribe to "the server says you are no longer signed in". Returns an unsubscribe. */
export function onSignedOut(listener: () => void): () => void {
    signedOutListeners.add(listener);
    return () => { signedOutListeners.delete(listener); };
}

function handleUnauthorized(path: string): void {
    // The auth endpoints answer 401 for ordinary reasons — a wrong password on
    // sign-in, for instance — and must not be read as "your session expired".
    if (path.startsWith('/api/auth/')) return;
    if (cachedUser === null && signedOutListeners.size === 0) return;
    cachedUser = null;
    signedOutListeners.forEach((fn) => fn());
}

// ---------------------------------------------------------------------------
// The client
// ---------------------------------------------------------------------------

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

function withQuery(path: string, params?: QueryParams): string {
    if (!params) return path;
    const search = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null || value === '') continue;
        search.set(key, String(value));
    }
    const qs = search.toString();
    return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path;
}

async function parse<T>(res: Response, path: string): Promise<T> {
    const text = await res.text();
    let body: unknown = null;
    if (text) {
        try {
            body = JSON.parse(text);
        } catch {
            body = null;
        }
    }

    if (!res.ok) {
        if (res.status === 401) handleUnauthorized(path);
        const message = (body as { error?: string } | null)?.error
            ?? (res.status === 404 ? 'Not found.' : 'Something went wrong. Please try again.');
        throw new ApiError(res.status, message);
    }
    return body as T;
}

async function send<T>(method: string, path: string, body?: unknown): Promise<T> {
    const init: RequestInit = {
        method,
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
    };
    if (body !== undefined) {
        init.headers = { ...init.headers, 'Content-Type': 'application/json' };
        init.body = JSON.stringify(body);
    }

    let res: Response;
    try {
        res = await fetch(path, init);
    } catch (err) {
        // fetch only rejects when the request never reached the server.
        throw new ApiError(0, errorMessage(err, 'No connection. Check your signal and try again.'));
    }
    return parse<T>(res, path);
}

export function apiGet<T>(path: string, params?: QueryParams): Promise<T> {
    return send<T>('GET', withQuery(path, params));
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
    return send<T>('POST', path, body ?? {});
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
    return send<T>('PATCH', path, body ?? {});
}

export function apiDelete<T>(path: string, body?: unknown): Promise<T> {
    return send<T>('DELETE', path, body);
}

// ---------------------------------------------------------------------------
// Uploads
// ---------------------------------------------------------------------------

/** Where an upload is filed. The server decides the bucket and who may use it. */
export type UploadKind =
    | 'evidence'
    | 'resolution'
    | 'garden'
    | 'avatar'
    /** An article's cover image. */
    | 'article'
    /** An article's attachment: a PDF or document, filed alongside the image. */
    | 'article-file'
    | 'referral'
    | 'document';

export interface UploadResult {
    /** The R2 object key. This is what gets stored on the row, never a URL. */
    key: string;
    size: number;
}

/**
 * POST a single file as a raw body to `/api/upload?kind=…`.
 *
 * XMLHttpRequest rather than fetch, because a resident on a phone uploading a
 * photo deserves a real progress bar and fetch still cannot report upload
 * progress.
 */
export function apiUpload(
    kind: UploadKind,
    file: Blob,
    onProgress?: (fraction: number) => void,
): Promise<UploadResult> {
    const path = `/api/upload?kind=${encodeURIComponent(kind)}`;
    return new Promise<UploadResult>((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('POST', path, true);
        xhr.withCredentials = true;
        xhr.responseType = 'text';
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.setRequestHeader('Accept', 'application/json');

        if (onProgress && xhr.upload) {
            xhr.upload.onprogress = (e) => {
                if (e.lengthComputable && e.total > 0) onProgress(e.loaded / e.total);
            };
        }

        xhr.onerror = () => reject(new ApiError(0, 'No connection. Check your signal and try again.'));
        xhr.onabort = () => reject(new ApiError(0, 'Upload cancelled.'));
        xhr.onload = () => {
            let body: { key?: string; size?: number; error?: string } | null = null;
            try {
                body = xhr.responseText ? JSON.parse(xhr.responseText) : null;
            } catch {
                body = null;
            }
            if (xhr.status >= 200 && xhr.status < 300 && body?.key) {
                onProgress?.(1);
                resolve({ key: body.key, size: body.size ?? file.size });
                return;
            }
            if (xhr.status === 401) handleUnauthorized(path);
            reject(new ApiError(xhr.status, body?.error ?? 'That file could not be uploaded.'));
        };

        xhr.send(file);
    });
}

// ---------------------------------------------------------------------------
// Error text
// ---------------------------------------------------------------------------

/**
 * Turn any thrown thing into a short sentence safe to show a resident.
 *
 * The Worker already writes friendly sentences, so an ApiError normally passes
 * straight through. The pattern matching below is for everything else — a
 * browser TypeError from a dropped connection, or a stray database message.
 */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
    if (!err) return fallback;
    const msg = typeof err === 'string' ? err : (err as { message?: string }).message;
    if (!msg) return fallback;
    if (/permission|policy/i.test(msg)) return "You don't have permission to do that.";
    if (/duplicate key|23505/.test(msg)) return 'That already exists.';
    if (/Failed to fetch|NetworkError|network/i.test(msg)) return 'No connection. Check your signal and try again.';
    return msg.length > 140 ? fallback : msg;
}
